import { format, setHours, setMinutes } from 'date-fns';

import { minutesToHours } from '@/helpers';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import {
  formatWallClockTime,
  normalizeToWallTimeUTC,
  parseWallClockToLocalDate,
} from '@/utils/date-formatters';

import { TimeEntryFormValues } from '../schema';
import { CreateWorklogRequest } from '../types/dto/worklogs-summary.dto';

/**
 * Parses time string in HH:mm format to hours and minutes
 * @param timeString - Time string in HH:mm format (e.g., "09:30")
 * @returns Object with hours and minutes, or null if invalid
 * @example
 * parseTimeString('09:30') // { hours: 9, minutes: 30 }
 * parseTimeString('23:59') // { hours: 23, minutes: 59 }
 * parseTimeString('invalid') // null
 */
export const parseTimeString = (timeString: string): { hours: number; minutes: number } | null => {
  if (!timeString || typeof timeString !== 'string') {
    return null;
  }

  // Match HH:mm format (00-23 hours, 00-59 minutes)
  const timeMatch = timeString.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!timeMatch) {
    return null;
  }

  const hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);

  // Additional validation
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
};

export const getDurationObject = (trackedHours?: number) => {
  if (!trackedHours || trackedHours < 0) return { hours: 0, minutes: 0 };
  const totalMinutes = Math.floor(trackedHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

// Convert TimeTrack to TimeEntry for edit mode.
// Worklog `date` is a wall-clock `...Z` timestamp — parse its literal components so
// the form's date picker and start-time field show exactly what the backend sent,
// regardless of the user's timezone. The startTime falls back to the literal time
// derived from `date` when the caller hasn't precomputed it.
export function convertTimeTrackToTimeEntry(track: any): TimeEntryFormValues {
  const duration = getDurationObject(minutesToHours(track.trackedMinutes ?? 0));

  return {
    projectId: String(track.project?.projectId ?? ''),
    date: parseWallClockToLocalDate(track.date ?? ''),
    startTime: track.startTime || (track.date ? formatWallClockTime(track.date) : ''),
    duration: duration,
    description: track.description || undefined,
  };
}

export const convertTimeEntryToTimeTrack = (
  entry: TimeEntryFormValues,
  project: DropdownSearchItem | null
): CreateWorklogRequest => {
  if (!project) {
    throw new Error('Project is required');
  }

  const durationInMinutes = entry.duration.hours * 60 + entry.duration.minutes;

  // Parse startTime (HH:mm format) and create a date with entry.date
  const parsedTime = parseTimeString(entry.startTime);
  if (!parsedTime) {
    throw new Error(`Invalid time format: ${entry.startTime}. Expected HH:mm format.`);
  }

  // The form holds `date` + `startTime` as a literal wall-clock value (see
  // convertTimeTrackToTimeEntry). Re-encode the same wall-clock as a `...Z`
  // instant so the digits round-trip unchanged — a plain `.toISOString()` would
  // shift them by the user's timezone offset.
  const localStartDateTime = setMinutes(setHours(entry.date, parsedTime.hours), parsedTime.minutes);
  const startDateTime = normalizeToWallTimeUTC(localStartDateTime);

  return {
    projectId: Number(project.id),
    startDate: startDateTime.toISOString(),
    trackedMinutes: Math.round(durationInMinutes),
    description: entry.description || '',
  };
};

// Format seconds to HH:MM:SS
export const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const getTime = (date: Date): string => {
  return format(date, 'HH:mm');
};

// Format hours display
export const formatHours = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Format hours for time entries (minutes precision)
export const formatTimeEntryDuration = (hours: number): string => {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

export interface DurationOverflow {
  date: string;
  currentDateHours: number;
  currentDateMinutes: number;
  overflowHours: number;
  overflowMinutes: number;
}

export const computeDurationOverflow = (
  startTime?: string,
  date?: Date,
  hours?: number,
  minutes?: number
): DurationOverflow | null => {
  if (!startTime || !date) return null;
  const parsed = startTime.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!parsed) return null;

  const startMinuteOfDay = parseInt(parsed[1], 10) * 60 + parseInt(parsed[2], 10);
  const totalDurationMinutes = (hours ?? 0) * 60 + (minutes ?? 0);
  if (totalDurationMinutes === 0) return null;

  const minutesUntilMidnight = 24 * 60 - startMinuteOfDay;
  if (totalDurationMinutes <= minutesUntilMidnight) return null;

  const overflowMinutes = totalDurationMinutes - minutesUntilMidnight;
  return {
    date: format(date, 'MMM d, yyyy'),
    currentDateHours: Math.floor(minutesUntilMidnight / 60),
    currentDateMinutes: minutesUntilMidnight % 60,
    overflowHours: Math.floor(overflowMinutes / 60),
    overflowMinutes: overflowMinutes % 60,
  };
};
